import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../api/axios';
import { toast } from 'react-toastify';
import { ArrowRight, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';

const steps = [
  { num: 1, label: 'Phone' },
  { num: 2, label: 'Personal' },
  { num: 3, label: 'Professional' },
  { num: 4, label: 'Address' },
];

const inp = `w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-gray-50
  focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent
  placeholder-gray-300 transition disabled:opacity-60 disabled:bg-gray-100`;

export default function Register() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [devOtp, setDevOtp] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [gender, setGender] = useState('');

  const [domains, setDomains] = useState([]);
  const [staffTypes, setStaffTypes] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState('');
  const [staffTypeId, setStaffTypeId] = useState('');
  const [qualification, setQualification] = useState('');
  const [experienceYears, setExperienceYears] = useState('');

  const [addressLine, setAddressLine] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [pincode, setPincode] = useState('');

  const handleSendOtp = async () => {
    if (!phone || phone.length < 10) { toast.error('Enter a valid 10-digit phone number'); return; }
    setLoading(true);
    try {
      const res = await authAPI.sendOtp(phone);
      setOtpSent(true);
      toast.success('OTP sent!');
      const match = res.data.data.message.match(/OTP: (\d{6})/);
      if (match) setDevOtp(match[1]);
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to send OTP'); }
    finally { setLoading(false); }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) { toast.error('Enter the 6-digit OTP'); return; }
    setLoading(true);
    try {
      await authAPI.verifyOtp(phone, otp);
      setOtpVerified(true);
      toast.success('Phone verified!');
      const dr = await authAPI.getDomains();
      setDomains(dr.data.data);
    } catch (e) { toast.error(e.response?.data?.message || 'Invalid OTP'); }
    finally { setLoading(false); }
  };

  const handleDomainChange = async (domainId) => {
    setSelectedDomain(domainId);
    setStaffTypeId('');
    if (domainId) {
      try { const r = await authAPI.getStaffTypes(domainId); setStaffTypes(r.data.data); }
      catch { toast.error('Failed to load staff types'); }
    } else { setStaffTypes([]); }
  };

  const handleRegister = async () => {
    setLoading(true);
    try {
      const res = await authAPI.register({
        phone, password, firstName, lastName, email, dateOfBirth, gender,
        staffTypeId: parseInt(staffTypeId), qualification,
        experienceYears: parseInt(experienceYears), addressLine, city, state, pincode
      });
      login(res.data.data);
      toast.success('Account created! Please upload your documents.');
      navigate('/complete-registration');
    } catch (e) { toast.error(e.response?.data?.message || 'Registration failed'); }
    finally { setLoading(false); }
  };

  const nextStep = () => {
    if (step === 1 && !otpVerified) { toast.error('Verify your phone first'); return; }
    if (step === 2) {
      if (!firstName || !lastName || !password || !dateOfBirth || !gender) { toast.error('Fill all required fields'); return; }
      if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }
      if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    }
    if (step === 3 && (!staffTypeId || !qualification || !experienceYears)) { toast.error('Fill all required fields'); return; }
    setStep(step + 1);
  };

  return (
    <div className="min-h-screen flex bg-white">
      {/* LEFT */}
      <div className="hidden lg:flex w-[40%] flex-col justify-between px-14 py-12
        bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 20% 60%, rgba(20,184,166,0.18) 0%, transparent 65%)' }} />
        <div className="relative">
          <img src="/logo-white.png" alt="ShramSena" className="h-10 w-auto" />
        </div>
        <div className="relative space-y-5">
          <p className="text-teal-400 text-xs font-bold uppercase tracking-[0.2em]">Staff Registration</p>
          <h2 className="text-3xl font-bold text-white leading-snug">
            Join India's healthcare<br />
            <span className="text-teal-400">staffing network.</span>
          </h2>
          <p className="text-slate-400 text-sm leading-relaxed">
            Create your verified profile in 4 simple steps and get discovered by hospitals across India.
          </p>
          <div className="space-y-2.5 pt-2">
            {steps.map((s) => (
              <div key={s.num} className={`flex items-center gap-3 transition-all ${step >= s.num ? 'opacity-100' : 'opacity-30'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0
                  ${step > s.num ? 'bg-teal-500 text-white' : step === s.num ? 'bg-white text-slate-900' : 'bg-white/20 text-white'}`}>
                  {step > s.num ? '✓' : s.num}
                </div>
                <span className={`text-sm font-medium ${step >= s.num ? 'text-white' : 'text-slate-500'}`}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="relative text-slate-600 text-xs">© {new Date().getFullYear()} ShramSena™</p>
      </div>

      <div className="hidden lg:block w-px bg-gradient-to-b from-transparent via-gray-200 to-transparent" />

      {/* RIGHT */}
      <div className="flex-1 flex flex-col justify-center px-8 sm:px-10 lg:px-14 py-10 overflow-y-auto">
        <div className="w-full max-w-md mx-auto">

          <div className="lg:hidden mb-6">
            <img src="/logo-horizontal.png" alt="ShramSena" className="h-8 w-auto" />
          </div>
          <div className="flex items-center gap-2 mb-6 lg:hidden">
            {steps.map((s) => (
              <div key={s.num} className={`h-1.5 rounded-full transition-all
                ${step === s.num ? 'w-6 bg-teal-600' : step > s.num ? 'w-4 bg-teal-300' : 'w-4 bg-gray-200'}`} />
            ))}
          </div>

          {/* STEP 1 */}
          {step === 1 && (
            <div className="space-y-5">
              <div>
                <p className="text-teal-600 text-xs font-bold uppercase tracking-[0.15em] mb-1">Step 1 of 4</p>
                <h1 className="text-2xl font-bold text-slate-900">Verify your phone</h1>
                <p className="text-gray-400 text-sm mt-1">We'll send a 6-digit OTP to confirm your number</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Phone Number *</label>
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                  placeholder="9876543210" disabled={otpVerified} maxLength={10} className={inp} />
              </div>
              {!otpSent && !otpVerified && (
                <button onClick={handleSendOtp} disabled={loading}
                  className="w-full py-3 rounded-xl font-bold text-sm text-white bg-gradient-to-r from-teal-600 to-cyan-600
                    hover:opacity-90 flex items-center justify-center gap-2 disabled:opacity-60 transition shadow-lg shadow-teal-500/20">
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending...</> : 'Send OTP'}
                </button>
              )}
              {otpSent && !otpVerified && (
                <>
                  {devOtp && (
                    <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5">
                      <span className="text-xs text-amber-700 font-medium">Dev OTP:</span>
                      <span className="text-sm font-black text-amber-800 tracking-[0.3em]">{devOtp}</span>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">Enter OTP *</label>
                    <input value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="123456" maxLength={6} className={inp} />
                  </div>
                  <button onClick={handleVerifyOtp} disabled={loading}
                    className="w-full py-3 rounded-xl font-bold text-sm text-white bg-gradient-to-r from-teal-600 to-cyan-600
                      hover:opacity-90 flex items-center justify-center gap-2 disabled:opacity-60 transition">
                    {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Verifying...</> : 'Verify OTP'}
                  </button>
                </>
              )}
              {otpVerified && (
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                  <CheckCircle className="h-5 w-5 text-green-600 shrink-0" />
                  <span className="text-sm font-semibold text-green-700">Phone verified!</span>
                </div>
              )}
            </div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <div className="space-y-4">
              <div>
                <p className="text-teal-600 text-xs font-bold uppercase tracking-[0.15em] mb-1">Step 2 of 4</p>
                <h1 className="text-2xl font-bold text-slate-900">Personal details</h1>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">First Name *</label>
                  <input value={firstName} onChange={(e) => setFirstName(e.target.value)} className={inp} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Last Name *</label>
                  <input value={lastName} onChange={(e) => setLastName(e.target.value)} className={inp} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email (optional)</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className={inp} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Password *</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 8 characters" className={inp} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Confirm Password *</label>
                <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className={inp} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Date of Birth *</label>
                  <input type="date" value={dateOfBirth} onChange={(e) => setDateOfBirth(e.target.value)} className={inp} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Gender *</label>
                  <select value={gender} onChange={(e) => setGender(e.target.value)} className={inp}>
                    <option value="">Select</option>
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3 */}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <p className="text-teal-600 text-xs font-bold uppercase tracking-[0.15em] mb-1">Step 3 of 4</p>
                <h1 className="text-2xl font-bold text-slate-900">Professional details</h1>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Domain *</label>
                <select value={selectedDomain} onChange={(e) => handleDomainChange(e.target.value)} className={inp}>
                  <option value="">Select Domain</option>
                  {domains.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Staff Type *</label>
                <select value={staffTypeId} onChange={(e) => setStaffTypeId(e.target.value)} disabled={!selectedDomain} className={inp}>
                  <option value="">Select Staff Type</option>
                  {staffTypes.map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Qualification *</label>
                <input value={qualification} onChange={(e) => setQualification(e.target.value)}
                  placeholder="e.g., GNM, BSc Nursing" className={inp} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Experience (years) *</label>
                <input type="number" value={experienceYears} onChange={(e) => setExperienceYears(e.target.value)}
                  min="0" max="50" placeholder="0" className={inp} />
              </div>
            </div>
          )}

          {/* STEP 4 */}
          {step === 4 && (
            <div className="space-y-4">
              <div>
                <p className="text-teal-600 text-xs font-bold uppercase tracking-[0.15em] mb-1">Step 4 of 4</p>
                <h1 className="text-2xl font-bold text-slate-900">Your address</h1>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Address Line *</label>
                <input value={addressLine} onChange={(e) => setAddressLine(e.target.value)}
                  placeholder="House/Flat, Street, Area" className={inp} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">City *</label>
                  <input value={city} onChange={(e) => setCity(e.target.value)} className={inp} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">State *</label>
                  <input value={state} onChange={(e) => setState(e.target.value)} className={inp} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Pincode *</label>
                <input value={pincode} onChange={(e) => setPincode(e.target.value)} maxLength={6} className={inp} />
              </div>
            </div>
          )}

          {/* NAV BUTTONS */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100">
            {step > 1 ? (
              <button onClick={() => setStep(step - 1)}
                className="flex items-center gap-1.5 text-gray-500 hover:text-gray-700 text-sm font-semibold transition">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
            ) : (
              <Link to="/login" className="text-sm text-gray-400 hover:text-teal-600 transition">
                Already have an account?
              </Link>
            )}
            {step < 4 ? (
              <button onClick={nextStep}
                className="flex items-center gap-1.5 bg-gradient-to-r from-teal-600 to-cyan-600 text-white
                  px-6 py-2.5 rounded-xl font-bold text-sm hover:opacity-90 transition shadow-lg shadow-teal-500/20">
                Next <ArrowRight className="h-4 w-4" />
              </button>
            ) : (
              <button onClick={handleRegister} disabled={loading || !addressLine || !city || !state || !pincode}
                className="flex items-center gap-1.5 bg-gradient-to-r from-teal-600 to-cyan-600 text-white
                  px-6 py-2.5 rounded-xl font-bold text-sm hover:opacity-90 transition shadow-lg shadow-teal-500/20 disabled:opacity-60">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating...</> : <><CheckCircle className="h-4 w-4" /> Create Account</>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
